import { emptyTaskState, PlanState, TaskEvent, TaskItem, TaskMessage, TaskState, TaskStatus, TeamAgent, TeamConflict, TeamDecision, TeamMessage, TeamRoomState } from './types'

export type TaskAction =
  | { type: 'event'; event: TaskEvent }
  | { type: 'replay'; events: TaskEvent[] }
  | { type: 'queue' }
  | { type: 'unread'; value: number }
  | { type: 'scroll'; value: number }
  | { type: 'draft'; value: string }

const bySequence = (left: TaskEvent, right: TaskEvent) => left.sequence - right.sequence || left.timestamp - right.timestamp || left.eventId.localeCompare(right.eventId)
const textFrom = (value: unknown) => typeof value === 'string' ? value : ''
const recordFrom = (value: unknown) => value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : null

function teamAgentFrom(value: unknown): TeamAgent | null {
  const source = recordFrom(value)
  if (!source || typeof source.id !== 'string' || !source.id) return null
  const { prompt, promptFile, sessionToken, secret, routing, modelPolicy, ...safe } = source
  return { ...safe, id: source.id, displayName: typeof source.displayName === 'string' ? source.displayName : undefined, role: typeof source.role === 'string' ? source.role : undefined, title: typeof source.title === 'string' ? source.title : undefined, state: typeof source.state === 'string' ? source.state : undefined }
}

function teamMessageFrom(value: unknown): TeamMessage | null {
  const source = recordFrom(value)
  if (!source || typeof source.id !== 'string' || typeof source.text !== 'string') return null
  const from = recordFrom(source.from)
  if (!from || !['user', 'agent', 'system'].includes(String(from.type)) || typeof from.id !== 'string') return null
  const to = recordFrom(source.to)
  return {
    ...source,
    id: source.id,
    sequence: typeof source.sequence === 'number' ? source.sequence : 0,
    from: { type: from.type as TeamMessage['from']['type'], id: from.id },
    to: to && typeof to.mode === 'string' ? { mode: to.mode as NonNullable<TeamMessage['to']>['mode'], ids: Array.isArray(to.ids) ? to.ids.filter((id): id is string => typeof id === 'string').slice(0, 32) : [] } : undefined,
    kind: typeof source.kind === 'string' ? source.kind : 'finding',
    text: source.text,
    preview: typeof source.preview === 'string' ? source.preview : source.text,
  }
}

function teamRecordFrom(value: unknown, fallback: string): TeamDecision | TeamConflict | null {
  const source = recordFrom(value)
  if (!source || typeof source.id !== 'string' || !source.id) return null
  return { ...source, id: source.id, statement: typeof source.statement === 'string' ? source.statement : undefined, summary: typeof source.summary === 'string' ? source.summary : undefined, status: typeof source.status === 'string' ? source.status : fallback }
}

function replaceById<T extends { id: string }>(items: T[], next: T): T[] {
  const index = items.findIndex((item) => item.id === next.id)
  if (index < 0) return [...items, next]
  const copy = items.slice()
  copy[index] = { ...copy[index], ...next }
  return copy
}

function teamFromEvent(event: TaskEvent, current?: TeamRoomState): TeamRoomState | undefined {
  const payload = event.payload
  const graph = recordFrom(payload.graph)
  const workflow = recordFrom(payload.workflow)
  const workflowId = String(event.workflowId ?? workflow?.id ?? current?.workflowId ?? '')
  if (!workflowId && !current) return undefined
  const next: TeamRoomState = current ? { ...current, agents: current.agents.slice(), messages: current.messages.slice(), decisions: current.decisions.slice(), conflicts: current.conflicts.slice() } : {
    workflowId,
    roomSequence: 0,
    agents: [],
    messages: [],
    decisions: [],
    conflicts: [],
  }
  next.workflowId = workflowId || next.workflowId
  next.updatedAt = event.timestamp
  if (graph) {
    next.graphId = typeof graph.id === 'string' ? graph.id : next.graphId
    next.title = typeof graph.title === 'string' ? graph.title : next.title
    next.state = typeof graph.state === 'string' ? graph.state : next.state
    next.version = typeof graph.version === 'number' ? graph.version : next.version
    next.roomSequence = typeof graph.roomSequence === 'number' ? Math.max(next.roomSequence, graph.roomSequence) : next.roomSequence
    if (Array.isArray(graph.agents)) next.agents = graph.agents.map(teamAgentFrom).filter((item): item is TeamAgent => Boolean(item))
    if (Array.isArray(graph.messages)) next.messages = graph.messages.map(teamMessageFrom).filter((item): item is TeamMessage => Boolean(item))
  }
  if (workflow) {
    next.title = typeof workflow.title === 'string' ? workflow.title : next.title
    next.state = typeof workflow.state === 'string' ? workflow.state : next.state
    if (Array.isArray(workflow.decisions)) next.decisions = workflow.decisions.map((item) => teamRecordFrom(item, 'active')).filter((item): item is TeamDecision => Boolean(item))
    if (Array.isArray(workflow.conflicts)) next.conflicts = workflow.conflicts.map((item) => teamRecordFrom(item, 'open')).filter((item): item is TeamConflict => Boolean(item))
  }
  const agent = teamAgentFrom(payload.agent)
  if (agent) next.agents = replaceById(next.agents, agent)
  const message = teamMessageFrom(payload.message)
  if (message) { next.messages = replaceById(next.messages, message); next.roomSequence = Math.max(next.roomSequence, message.sequence) }
  const decision = teamRecordFrom(payload.decision, 'active') as TeamDecision | null
  if (decision) next.decisions = replaceById(next.decisions, decision)
  const conflict = teamRecordFrom(payload.conflict, 'open') as TeamConflict | null
  if (conflict) next.conflicts = replaceById(next.conflicts, conflict)
  if (typeof payload.conflictId === 'string' && payload.event === 'conflict-resolved') {
    const resolved = next.conflicts.find((item) => item.id === payload.conflictId)
    if (resolved) resolved.status = 'resolved'
  }
  return next
}

function planFromEvent(event: TaskEvent, current?: PlanState): PlanState | undefined {
  if (event.kind === 'team') {
    const team = teamFromEvent(event, current?.team)
    if (!team) return current
    if (!current) return { id: team.workflowId, title: team.title || 'Team workflow', status: team.state || 'pending', source: 'workflow', steps: [], team }
    return { ...current, team, agents: team.agents.map((agent) => ({ id: agent.id, profileId: agent.profileId, role: agent.role, title: agent.title || agent.displayName, model: agent.model, state: agent.state, statusSummary: agent.statusSummary, assignedStepId: agent.assignedStepId, avatar: agent.avatar })) }
  }
  if (event.kind === 'agent') {
    const graph = recordFrom(event.payload.graph)
    const agents = Array.isArray(graph?.agents) ? graph.agents.map((raw) => {
      const agent = recordFrom(raw) ?? {}
      return { id: String(agent.id ?? ''), profileId: typeof agent.profileId === 'string' ? agent.profileId : null, role: typeof agent.role === 'string' ? agent.role : undefined, title: typeof agent.title === 'string' ? agent.title : undefined, model: typeof agent.model === 'string' ? agent.model : undefined, state: typeof agent.state === 'string' ? agent.state : undefined, statusSummary: typeof agent.statusSummary === 'string' ? agent.statusSummary : undefined, assignedStepId: typeof agent.assignedStepId === 'string' ? agent.assignedStepId : null, avatar: recordFrom(agent.avatar) }
    }).filter((agent) => agent.id) : []
    const team = graph ? teamFromEvent({ ...event, kind: 'team' }, current?.team) : current?.team
    if (!current && !team) return current
    return current
      ? { ...current, agents, team, updatedAt: event.timestamp }
      : { id: team!.workflowId, title: team!.title || 'Team workflow', status: team!.state || 'pending', source: 'workflow', steps: [], team }
  }
  if (!['plan', 'workflow'].includes(event.kind)) return current
  const payload = event.payload
  const candidate = recordFrom(payload.workflow) ?? recordFrom(payload.plan) ?? (event.kind === 'plan' ? payload : null)
  if (!candidate) return current
  const id = String(candidate.id ?? event.workflowId ?? current?.id ?? `${event.taskId}-plan`)
  const rawSteps = Array.isArray(candidate.steps) ? candidate.steps : Array.isArray(candidate.items) ? candidate.items : []
  const steps = rawSteps.map((raw, index) => {
    const step = recordFrom(raw) ?? {}
    const execution = recordFrom(step.execution)
    return {
      id: String(step.id ?? `step-${index + 1}`),
      title: String(step.title ?? step.name ?? step.task ?? `Step ${index + 1}`),
      description: typeof step.description === 'string' ? step.description : typeof step.rationale === 'string' ? step.rationale : undefined,
      status: String(step.status ?? 'pending'),
      attempts: typeof step.attempts === 'number' ? step.attempts : undefined,
      retries: typeof step.retries === 'number' ? step.retries : undefined,
      checks: typeof step.checks === 'string' ? step.checks : Array.isArray(step.checks) ? step.checks.filter((check): check is string => typeof check === 'string').slice(0, 8).join(' · ') : undefined,
      review: typeof step.review === 'string' ? step.review : recordFrom(step.review)?.summary as string | undefined,
      owner: execution ? { agent: typeof execution.agent === 'string' ? execution.agent : undefined, provider: typeof execution.providerId === 'string' ? execution.providerId : undefined, model: typeof execution.model === 'string' ? execution.model : undefined } : undefined,
      files: Array.isArray(step.files) ? step.files.filter((file): file is string => typeof file === 'string').slice(0, 20) : undefined,
      commands: Array.isArray(step.commands) ? step.commands.filter((command): command is string => typeof command === 'string').slice(0, 20) : undefined,
    }
  })
  const metadata = recordFrom(candidate.metadata)
  const owner = recordFrom(candidate.owner) ?? recordFrom(candidate.execution) ?? metadata
  return {
    id,
    title: String(candidate.title ?? current?.title ?? 'Implementation plan'),
    status: String(candidate.state ?? candidate.status ?? current?.status ?? 'pending'),
    source: event.kind === 'workflow' || event.source === 'workflow' ? 'workflow' : 'codex',
    owner: owner ? { agent: typeof owner.agent === 'string' ? owner.agent : undefined, provider: typeof owner.providerId === 'string' ? owner.providerId : undefined, model: typeof owner.model === 'string' ? owner.model : typeof owner.supervisorModel === 'string' ? owner.supervisorModel : undefined } : current?.owner,
    explanation: typeof metadata?.planSummary === 'string' ? metadata.planSummary : typeof candidate.explanation === 'string' ? candidate.explanation : current?.explanation,
    activeStepId: typeof candidate.currentStepId === 'string' ? candidate.currentStepId : current?.activeStepId,
    steps: steps.length ? steps : current?.steps ?? [],
    checks: typeof candidate.checks === 'string' ? candidate.checks : current?.checks,
    review: recordFrom(candidate.review) ? { decision: typeof recordFrom(candidate.review)?.decision === 'string' ? String(recordFrom(candidate.review)?.decision) : undefined, summary: typeof recordFrom(candidate.review)?.summary === 'string' ? String(recordFrom(candidate.review)?.summary) : undefined } : current?.review,
    updatedAt: candidate.updatedAt as string | number | undefined ?? event.timestamp,
  }
}

function itemFrom(event: TaskEvent): TaskItem | null {
  const candidate = event.payload.item
  if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) return event.itemId ? { id: event.itemId, type: event.kind, status: typeof event.payload.status === 'string' ? event.payload.status : undefined, event } : null
  const item = candidate as Record<string, unknown>
  const id = typeof item.id === 'string' ? item.id : event.itemId
  return id ? { id, type: typeof item.type === 'string' ? item.type : event.kind, status: typeof item.status === 'string' ? item.status : undefined, event } : null
}

function derive(state: TaskState, events: TaskEvent[]): TaskState {
  const sorted = [...events].sort(bySequence)
  const itemMap = new Map<string, TaskItem>()
  const completedItems = new Set<string>()
  const messageMap = new Map<string, TaskMessage>()
  const completedMessages = new Set<string>()
  let status: TaskStatus = state.status === 'queued' ? 'queued' : 'idle'
  let activeTurnId: string | undefined
  let running = false
  let plan = state.plan
  for (const event of sorted) {
    const method = event.method ?? ''
    const payload = event.payload
    plan = planFromEvent(event, plan)
    if (method === 'turn/started') {
      activeTurnId = event.turnId ?? (typeof (payload.turn as Record<string, unknown> | undefined)?.id === 'string' ? String((payload.turn as Record<string, unknown>).id) : undefined)
      running = true
      status = 'running'
    } else if (method === 'turn/completed') {
      const turn = payload.turn as Record<string, unknown> | undefined
      const turnStatus = typeof turn?.status === 'string' ? turn.status : typeof payload.status === 'string' ? payload.status : 'completed'
      running = false
      activeTurnId = undefined
      status = turnStatus === 'interrupted' ? 'interrupted' : turnStatus === 'failed' || turnStatus === 'error' ? 'failed' : turnStatus === 'cancelled' ? 'cancelled' : 'completed'
    }
    const item = itemFrom(event)
    if (item) {
      if (method === 'item/completed') { completedItems.add(item.id); itemMap.set(item.id, item) }
      else if (!completedItems.has(item.id)) itemMap.set(item.id, item)
    }
    const rawItem = payload.item as Record<string, unknown> | undefined
    const itemType = typeof rawItem?.type === 'string' ? rawItem.type : ''
    const messageId = event.itemId ?? (typeof rawItem?.id === 'string' ? rawItem.id : undefined)
    if (itemType === 'userMessage' || itemType === 'agentMessage' || (event.kind === 'delta' && event.method === 'item/agentMessage/delta')) {
      const role: TaskMessage['role'] = itemType === 'userMessage' || payload.role === 'user' ? 'user' : 'assistant'
      const id = messageId ?? `${role}-${event.threadId ?? event.taskId}`
      const phase = (typeof rawItem?.phase === 'string' ? rawItem.phase : typeof payload.phase === 'string' ? payload.phase : undefined) as TaskMessage['phase']
      const isCompletedMessage = method === 'item/completed' && (itemType === 'agentMessage' || itemType === 'userMessage')
      if (isCompletedMessage) {
        const content = textFrom(payload.text) || textFrom(rawItem?.text)
        if (content) { messageMap.set(id, { id, role, content, streaming: false, phase }); completedMessages.add(id) }
      } else if (event.kind === 'delta' && !completedMessages.has(id)) {
        const delta = textFrom(payload.text) || textFrom(payload.delta)
        if (delta) { const existing = messageMap.get(id); messageMap.set(id, { id, role, content: `${existing?.content ?? ''}${delta}`, streaming: true, phase: phase ?? existing?.phase }) }
      }
    }
  }
  const planRunning = Boolean(plan && ['draft', 'replanning', 'running_step', 'testing', 'reviewing', 'running', 'in_progress'].includes(plan.status))
  if (planRunning && !running) { running = true; status = 'running' }
  if (plan && ['completed', 'failed', 'cancelled', 'paused', 'escalated'].includes(plan.status) && !sorted.some((event) => event.method === 'turn/completed')) {
    running = false
    status = plan.status === 'cancelled' ? 'cancelled' : plan.status === 'failed' || plan.status === 'escalated' ? 'failed' : 'completed'
  }
  return { ...state, events: sorted, seen: new Set(sorted.map((event) => event.eventId)), items: [...itemMap.values()], messages: [...messageMap.values()], plan, status, running, activeTurnId }
}

export function taskReducer(state: TaskState, action: TaskAction): TaskState {
  if (action.type === 'queue') return { ...state, status: 'queued', running: true }
  if (action.type === 'unread') return { ...state, unread: Math.max(0, Math.floor(action.value)) }
  if (action.type === 'scroll') return { ...state, scrollTop: Math.max(0, action.value) }
  if (action.type === 'draft') return { ...state, draft: action.value }
  const incoming = action.type === 'replay' ? action.events : [action.event]
  const merged = [...state.events]
  for (const event of incoming) if (event.taskId === state.taskId && !merged.some((current) => current.eventId === event.eventId)) merged.push(event)
  return derive(state, merged)
}

export const createTaskState = emptyTaskState
